import { el, svg } from "../core/dom";
import { styles } from "../styles/modal";
import type { Settings } from "../api/types";
import type { ResolvedColors } from "./colors";

const RELATIONSHIP_OPTIONS = [
  { value: "Father", label: "Father" },
  { value: "Mother", label: "Mother" },
  { value: "Legal Guardian", label: "Legal Guardian" },
  { value: "Grandparent", label: "Grandparent" },
];

export type GuardianFormData = {
  guardianName: string;
  guardianContact: string;
  guardianRelationship: string;
};

export type GuardianFormRefs = {
  container: HTMLElement;
  nameInput: HTMLInputElement;
  contactInput: HTMLInputElement;
  relationshipDisplay: HTMLElement;
  nameError: HTMLElement;
  contactError: HTMLElement;
  relationshipError: HTMLElement;
  submitBtn: HTMLButtonElement;
  generalError: HTMLElement;
};

export function renderGuardianForm(options: {
  formData: GuardianFormData;
  settings?: Partial<Settings>;
  colors: ResolvedColors;
  isMobile: boolean;
  logoUrl: string;
  isSubmitting: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  onFieldChange: (field: keyof GuardianFormData, value: string) => void;
}): GuardianFormRefs {
  const { formData, settings, colors, isMobile, logoUrl, isSubmitting, onSubmit, onCancel, onFieldChange } = options;

  const headingColor = settings?.headingColor || "#101828";
  const textColor = colors.text;
  const borderColor = colors.declineBorder;
  const bgColor = settings?.backgroundColor || "#ffffff";
  const radius = colors.borderRadius;
  const font = colors.font;
  const accentColor = colors.acceptBg;

  const inputStyle: Record<string, any> = {
    width: "100%",
    padding: "10px 12px",
    border: `1px solid ${borderColor}`,
    borderRadius: radius,
    fontSize: "13px",
    fontFamily: font,
    color: textColor,
    backgroundColor: bgColor,
    marginBottom: "2px",
    boxSizing: "border-box",
    outline: "none",
  };

  const errorStyle: Record<string, any> = {
    color: "#DC2626",
    fontSize: "11px",
    marginTop: "4px",
    marginBottom: "0px",
    display: "none",
  };

  const labelStyle: Record<string, any> = {
    fontSize: "13px",
    fontWeight: 500,
    color: textColor,
    fontFamily: font,
    marginBottom: "4px",
    display: "block",
  };

  const hintStyle: Record<string, any> = {
    fontSize: "11px",
    color: "#667085",
    marginTop: "2px",
    marginBottom: "0px",
    lineHeight: "1.4",
  };

  // ─── Top Section ───
  const logo = el("img", { src: logoUrl, style: styles.logo, alt: "Logo" });
  const title = el("h2", {
    id: "guardian-form-title",
    style: {
      ...styles.title,
      fontSize: "16px",
      fontWeight: 600,
      color: headingColor,
      fontFamily: font,
    },
  });
  title.textContent = "Guardian Verification";

  const topLeft = el("div", { style: styles.topLeft }, logo, title);
  const topSection = el("div", {
    style: { ...styles.topSection, color: textColor, fontFamily: font },
  }, topLeft);

  // ─── Middle Section ───
  const subtitle = el("p", {
    style: {
      ...styles.privacyText,
      fontSize: "13px",
      lineHeight: "1.5",
      color: textColor,
      fontFamily: font,
      marginBottom: "12px",
    },
  });
  subtitle.textContent = "Since you are under the age of consent, a legal guardian must verify their identity via DigiLocker before you can proceed.";

  // Info banner with SVG icon
  const infoIcon = svg(
    "svg",
    {
      width: "16", height: "16", viewBox: "0 0 24 24",
      fill: "none", stroke: "#2563EB", "stroke-width": "2",
      "stroke-linecap": "round", "stroke-linejoin": "round",
    },
    svg("circle", { cx: "12", cy: "12", r: "10" }),
    svg("line", { x1: "12", y1: "16", x2: "12", y2: "12" }),
    svg("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })
  );
  (infoIcon as unknown as HTMLElement).style.cssText = "flex-shrink:0;margin-top:1px";

  const infoText = el("span");
  infoText.textContent = "The guardian's name will be matched against their DigiLocker identity document (e.g. Aadhaar). Please enter the name exactly as it appears on the document.";

  const infoBanner = el("div", {
    style: {
      display: "flex",
      alignItems: "flex-start",
      gap: "8px",
      padding: "10px 12px",
      backgroundColor: "#EFF6FF",
      border: "1px solid #BFDBFE",
      borderRadius: radius,
      marginBottom: "16px",
      fontSize: "12px",
      lineHeight: "1.5",
      color: "#1E40AF",
    },
  }, infoIcon, infoText);

  // ─── Name field ───
  const nameLabel = el("label", { for: "guardianName", style: labelStyle });
  nameLabel.innerHTML = `Guardian's Full Name <span style="color:#DC2626">*</span>`;

  const nameInput = el("input", {
    id: "guardianName",
    type: "text",
    style: inputStyle,
    value: formData.guardianName,
    placeholder: "e.g. Rajesh Kumar",
    autoComplete: "name",
    "aria-describedby": "guardianName-hint",
    onInput: (e: Event) => onFieldChange("guardianName", (e.target as HTMLInputElement).value),
  }) as HTMLInputElement;

  const nameHint = el("p", { id: "guardianName-hint", style: hintStyle });
  nameHint.textContent = "Enter the full name as per government ID (Aadhaar, PAN, etc.)";

  const nameError = el("div", { style: errorStyle, role: "alert" });
  const nameGroup = el("div", null, nameLabel, nameInput, nameHint, nameError);

  // ─── Contact field ───
  const contactLabel = el("label", { for: "guardianContact", style: labelStyle });
  contactLabel.innerHTML = `Guardian's Phone Number <span style="color:#DC2626">*</span>`;

  const contactInput = el("input", {
    id: "guardianContact",
    type: "tel",
    inputMode: "tel",
    style: inputStyle,
    value: formData.guardianContact,
    placeholder: "e.g. 9876543210",
    autoComplete: "tel",
    "aria-describedby": "guardianContact-hint",
    onInput: (e: Event) => onFieldChange("guardianContact", (e.target as HTMLInputElement).value),
  }) as HTMLInputElement;

  const contactHint = el("p", { id: "guardianContact-hint", style: hintStyle });
  contactHint.textContent = "Mobile number linked to guardian's Aadhaar";

  const contactError = el("div", { style: errorStyle, role: "alert" });
  const contactGroup = el("div", null, contactLabel, contactInput, contactHint, contactError);

  // ─── Relationship dropdown ───
  const relLabel = el("label", { id: "guardianRelationship-label", style: labelStyle });
  relLabel.innerHTML = `Relationship <span style="color:#DC2626">*</span>`;

  const selectedLabel = RELATIONSHIP_OPTIONS.find((o) => o.value === formData.guardianRelationship)?.label;

  const chevronSvg = svg(
    "svg",
    {
      width: "14", height: "14", viewBox: "0 0 24 24",
      fill: "none", stroke: "#667085", "stroke-width": "2",
      "stroke-linecap": "round", "stroke-linejoin": "round",
    },
    svg("polyline", { points: "6 9 12 15 18 9" })
  );
  (chevronSvg as unknown as HTMLElement).style.cssText = "flex-shrink:0;margin-left:8px;transition:transform 0.2s";

  const selectedSpan = el("span", {
    style: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  });
  selectedSpan.textContent = selectedLabel || "Select relationship";

  const relationshipDisplay = el("div", {
    role: "combobox",
    "aria-expanded": "false",
    "aria-haspopup": "listbox",
    "aria-labelledby": "guardianRelationship-label",
    tabIndex: "0",
    style: {
      ...inputStyle,
      color: selectedLabel ? textColor : "#9CA3AF",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      userSelect: "none",
      transition: "border-color 0.15s, box-shadow 0.15s",
    },
  }, selectedSpan, chevronSvg);

  // Dropdown list
  const dropdownList = el("div", {
    id: "guardianRelationship-listbox",
    role: "listbox",
    "aria-labelledby": "guardianRelationship-label",
    style: {
      position: "absolute",
      top: "calc(100% + 4px)",
      left: 0,
      right: 0,
      backgroundColor: bgColor,
      border: `1px solid ${borderColor}`,
      borderRadius: radius,
      boxShadow: "0 4px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)",
      zIndex: 50,
      overflow: "hidden",
      padding: "4px 0",
      maxHeight: "220px",
      overflowY: "auto",
      display: "none",
    },
  });

  for (let i = 0; i < RELATIONSHIP_OPTIONS.length; i++) {
    const option = RELATIONSHIP_OPTIONS[i];
    const isSelected = formData.guardianRelationship === option.value;

    const optionEl = el("div", {
      id: `guardianRelationship-option-${i}`,
      role: "option",
      "aria-selected": String(isSelected),
      style: {
        padding: "9px 12px",
        fontSize: "13px",
        fontFamily: font,
        color: isSelected ? accentColor : textColor,
        backgroundColor: "transparent",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        transition: "background-color 0.1s",
      },
      onClick: () => {
        onFieldChange("guardianRelationship", option.value);
        selectedSpan.textContent = option.label;
        relationshipDisplay.style.color = textColor;
        doClose();
      },
      onMouseenter: (e: Event) => { (e.target as HTMLElement).style.backgroundColor = "#F3F4F6"; },
      onMouseleave: (e: Event) => { (e.target as HTMLElement).style.backgroundColor = "transparent"; },
    });

    const label = el("span");
    label.textContent = option.label;
    optionEl.appendChild(label);

    if (isSelected) {
      const checkSvg = svg(
        "svg",
        {
          width: "14", height: "14", viewBox: "0 0 24 24",
          fill: "none", stroke: accentColor, "stroke-width": "2.5",
          "stroke-linecap": "round", "stroke-linejoin": "round",
        },
        svg("polyline", { points: "20 6 9 17 4 12" })
      );
      (checkSvg as unknown as HTMLElement).style.flexShrink = "0";
      optionEl.appendChild(checkSvg);
    }

    dropdownList.appendChild(optionEl);
  }

  let dropdownOpen = false;

  const relContainer = el("div", { style: { position: "relative" } });

  const handleClickOutside = (e: Event) => {
    if (!relContainer.contains(e.target as Node)) {
      doClose();
    }
  };

  function doOpen() {
    dropdownOpen = true;
    dropdownList.style.display = "block";
    relationshipDisplay.setAttribute("aria-expanded", "true");
    (chevronSvg as unknown as HTMLElement).style.transform = "rotate(180deg)";
    relationshipDisplay.style.borderColor = accentColor;
    relationshipDisplay.style.boxShadow = `0 0 0 1px ${accentColor}`;

    // Determine direction: open upward if not enough space below
    const rect = relContainer.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 220;
    const shouldOpenUpward = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    if (shouldOpenUpward) {
      dropdownList.style.top = "auto";
      dropdownList.style.bottom = "calc(100% + 4px)";
      dropdownList.style.boxShadow = "0 -4px 12px rgba(0,0,0,0.1), 0 -1px 3px rgba(0,0,0,0.06)";
    } else {
      dropdownList.style.top = "calc(100% + 4px)";
      dropdownList.style.bottom = "auto";
      dropdownList.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)";
    }

    // Scroll the dropdown into view
    requestAnimationFrame(() => {
      dropdownList.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });

    // Listen for clicks outside (next tick so current click doesn't trigger it)
    setTimeout(() => document.addEventListener("mousedown", handleClickOutside), 0);
  }

  function doClose() {
    dropdownOpen = false;
    dropdownList.style.display = "none";
    relationshipDisplay.setAttribute("aria-expanded", "false");
    (chevronSvg as unknown as HTMLElement).style.transform = "rotate(0deg)";
    relationshipDisplay.style.borderColor = borderColor;
    relationshipDisplay.style.boxShadow = "none";
    document.removeEventListener("mousedown", handleClickOutside);
  }

  relationshipDisplay.addEventListener("click", () => {
    dropdownOpen ? doClose() : doOpen();
  });

  relationshipDisplay.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      e.preventDefault();
      if (!dropdownOpen) doOpen();
    } else if (e.key === "Escape" || e.key === "Tab") {
      doClose();
    }
  });

  const relationshipError = el("div", { style: errorStyle, role: "alert" });

  relContainer.appendChild(relLabel);
  relContainer.appendChild(relationshipDisplay);
  relContainer.appendChild(dropdownList);
  relContainer.appendChild(relationshipError);

  // General error
  const generalError = el("div", {
    role: "alert",
    style: {
      color: "#DC2626",
      fontSize: "13px",
      marginTop: "16px",
      padding: "12px",
      borderRadius: "6px",
      border: "1px solid #FCA5A5",
      backgroundColor: "#FEF2F2",
      textAlign: "center",
      display: "none",
    },
  });

  const fieldsContainer = el("div", {
    style: { display: "flex", flexDirection: "column", gap: "14px" },
  }, nameGroup, contactGroup, relContainer);

  const middleSection = el("div", {
    style: { ...styles.middleSection, overflow: "visible", color: textColor, fontFamily: font },
  }, subtitle, infoBanner, fieldsContainer, generalError);

  // ─── Bottom Section (Verify first, Cancel second — matches React) ───
  const btnBase = {
    ...styles.button,
    fontSize: isMobile ? "14px" : "16px",
    padding: isMobile ? "10px 20px" : "9px 45px",
  };

  const submitBtn = el("button", {
    style: {
      ...btnBase,
      ...styles.acceptButton,
      backgroundColor: colors.acceptBg,
      color: colors.acceptText,
      borderRadius: colors.borderRadius,
    },
    type: "button",
    disabled: isSubmitting,
    "aria-label": isSubmitting ? "Verifying guardian identity" : "Proceed to guardian verification",
    onClick: onSubmit,
  }) as HTMLButtonElement;
  submitBtn.textContent = isSubmitting ? "Verifying..." : "Verify via DigiLocker";

  const cancelBtn = el("button", {
    style: {
      ...btnBase,
      ...styles.cancelButton,
      backgroundColor: colors.declineBg,
      color: colors.declineText,
      borderColor: colors.declineBorder,
      borderRadius: colors.borderRadius,
    },
    type: "button",
    "aria-label": "Cancel",
    onClick: onCancel,
  });
  cancelBtn.textContent = "Cancel";

  const bottomSection = el("div", {
    style: { ...styles.bottomSection, paddingTop: "12px", color: textColor, fontFamily: font },
  }, submitBtn, cancelBtn);

  // ─── Assemble ───
  const container = el("div", { style: { ...styles.content, overflow: "visible" } }, topSection, middleSection, bottomSection);

  return {
    container,
    nameInput,
    contactInput,
    relationshipDisplay,
    nameError,
    contactError,
    relationshipError,
    submitBtn,
    generalError,
  };
}

export function validateGuardianForm(data: GuardianFormData): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!data.guardianName.trim()) errors.guardianName = "Guardian name is required";
  if (!data.guardianContact.trim()) errors.guardianContact = "Guardian contact is required";
  if (!data.guardianRelationship.trim()) errors.guardianRelationship = "Relationship to guardian is required";
  return errors;
}
